<template>
  <div class="auto-schedule-form">
    <!-- ========================================== -->
    <!-- فرم بازه -->
    <!-- ========================================== -->
    <div class="form-row">
      <div class="form-group">
        <label class="required">از تاریخ</label>
        <input
            v-model="fromDate"
            type="date"
            class="form-control"
        />
      </div>

      <div class="form-group">
        <label class="required">تا تاریخ</label>
        <input
            v-model="toDate"
            type="date"
            class="form-control"
        />
      </div>
    </div>

    <div class="info-box info">
      📌 حداکثر بازه <strong>۷ روز</strong> است.
    </div>

    <!-- ========================================== -->
    <!-- دکمه پیش‌نمایش -->
    <!-- ========================================== -->
    <div class="form-actions">
      <button
          class="btn-secondary"
          :disabled="previewing || !canPreview"
          @click="preview"
      >
        {{ previewing ? '⏳ در حال بررسی...' : '🔍 پیش‌نمایش' }}
      </button>
    </div>

    <!-- ========================================== -->
    <!-- نتیجه پیش‌نمایش -->
    <!-- ========================================== -->
    <div v-if="previewResult" class="preview-result">
      <div class="preview-header">
        <h4>📊 نتیجه پیش‌نمایش</h4>
      </div>

      <div class="preview-summary">
        <div class="summary-item success">
          <span class="summary-value">{{ previewResult.total_days }}</span>
          <span class="summary-label">روز قابل ایجاد</span>
        </div>
        <div class="summary-item warning">
          <span class="summary-value">{{ previewResult.skipped_days }}</span>
          <span class="summary-label">روز رد شده</span>
        </div>
        <div class="summary-item info">
          <span class="summary-value">{{ previewResult.total_slots }}</span>
          <span class="summary-label">نوبت جدید</span>
        </div>
      </div>

      <div class="preview-days">
        <div
            v-for="day in previewResult.days"
            :key="day.date"
            :class="['preview-day', { 'can-create': day.can_create }]"
        >
          <div class="preview-day-header">
            <span class="preview-day-label">{{ day.day_label }}</span>
            <span class="preview-day-date">{{ formatDate(day.date) }}</span>
            <span :class="['preview-day-badge', day.can_create ? 'badge-success' : 'badge-warning']">
              {{ day.can_create ? '✓ ' + day.slots_count + ' نوبت' : '✗' }}
            </span>
          </div>
          <div class="preview-day-reason">{{ day.reason }}</div>
        </div>
      </div>

      <!-- ========================================== -->
      <!-- دکمه ایجاد -->
      <!-- ========================================== -->
      <div v-if="previewResult.total_slots > 0" class="form-actions">
        <button
            class="btn-primary"
            :disabled="creating"
            @click="create"
        >
          {{ creating ? '⏳ در حال ایجاد...' : '✅ ایجاد ' + previewResult.total_slots + ' نوبت' }}
        </button>
      </div>

      <div v-else class="empty-preview">
        ⚠️ هیچ نوبتی برای ایجاد وجود ندارد.
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'AutoScheduleForm',

  props: {
    adminId: {
      type: Number,
      required: true
    },
    lineId: {
      type: Number,
      required: true
    }
  },

  data: function() {
    return {
      fromDate: '',
      toDate: '',
      previewing: false,
      creating: false,
      previewResult: null
    };
  },

  computed: {
    canPreview: function() {
      return this.fromDate && this.toDate && this.fromDate <= this.toDate;
    }
  },

  mounted: function() {
    // ==========================================
    // پیش‌فرض: از فردا تا ۷ روز آینده
    // ==========================================
    var tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);

    var nextWeek = new Date();
    nextWeek.setDate(nextWeek.getDate() + 7);

    this.fromDate = this.formatDateForInput(tomorrow);
    this.toDate = this.formatDateForInput(nextWeek);
  },

  methods: {
    // ==========================================
    // پیش‌نمایش
    // ==========================================
    preview: async function() {
      if (!this.canPreview) return;

      this.previewing = true;
      this.previewResult = null;

      try {
        var formData = new FormData();
        formData.append('admin_id', this.adminId);
        formData.append('line_id', this.lineId);
        formData.append('from_date', this.fromDate);
        formData.append('to_date', this.toDate);

        var response = await axios.post('/api/admin/auto-schedule/preview', formData);
        this.previewResult = response.data;

      } catch (error) {
        var msg = 'خطا در پیش‌نمایش';
        if (error.response && error.response.data && error.response.data.message) {
          msg = error.response.data.message;
        }
        this.showError(msg);
      } finally {
        this.previewing = false;
      }
    },

    // ==========================================
    // ایجاد
    // ==========================================
    create: async function() {
      if (!this.previewResult || this.previewResult.total_slots === 0) return;

      if (!confirm('آیا از ایجاد ' + this.previewResult.total_slots + ' نوبت اطمینان دارید؟')) {
        return;
      }

      this.creating = true;

      try {
        var formData = new FormData();
        formData.append('admin_id', this.adminId);
        formData.append('line_id', this.lineId);
        formData.append('from_date', this.fromDate);
        formData.append('to_date', this.toDate);

        var response = await axios.post('/api/admin/auto-schedule/create', formData);

        this.$emit('created', response.data);
        this.previewResult = null;

      } catch (error) {
        var msg = 'خطا در ایجاد';
        if (error.response && error.response.data && error.response.data.message) {
          msg = error.response.data.message;
        }
        this.showError(msg);
      } finally {
        this.creating = false;
      }
    },

    // ==========================================
    // فرمت‌دهی
    // ==========================================
    formatDate: function(dateStr) {
      if (!dateStr) return '';
      var d = new Date(dateStr);
      return d.getFullYear() + '/' +
          String(d.getMonth() + 1).padStart(2, '0') + '/' +
          String(d.getDate()).padStart(2, '0');
    },

    formatDateForInput: function(date) {
      return date.getFullYear() + '-' +
          String(date.getMonth() + 1).padStart(2, '0') + '-' +
          String(date.getDate()).padStart(2, '0');
    },

    showSuccess: function(msg) {
      if (this.$toast) this.$toast.success(msg);
      else alert(msg);
    },

    showError: function(msg) {
      if (this.$toast) this.$toast.error(msg);
      else alert(msg);
    }
  }
};
</script>

<style scoped>
.auto-schedule-form {
  width: 100%;
}

.form-row {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
}

.form-group {
  flex: 1;
}

.form-group label {
  display: block;
  font-weight: 600;
  font-size: 14px;
  color: #2c3e50;
  margin-bottom: 8px;
}

.form-group label.required::after {
  content: '*';
  color: #f56c6c;
  margin-right: 4px;
}

.form-control {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
}

.form-control:focus {
  outline: none;
  border-color: #409eff;
}

.info-box {
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 13px;
  margin-bottom: 16px;
}

.info-box.info {
  background: #ecf5ff;
  border: 1px solid #b3d8ff;
  color: #409eff;
}

.form-actions {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.btn-primary {
  padding: 12px 28px;
  background: #409eff;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary:hover:not(:disabled) {
  background: #66b1ff;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3);
}

.btn-primary:disabled {
  background: #a0cfff;
  cursor: not-allowed;
}

.btn-secondary {
  padding: 12px 28px;
  background: #f5f7fa;
  color: #606266;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-secondary:hover:not(:disabled) {
  background: #e4e7ed;
}

.btn-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ========================================== */
/* نتیجه پیش‌نمایش */
/* ========================================== */
.preview-result {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 2px solid #f0f0f0;
}

.preview-header {
  margin-bottom: 16px;
}

.preview-header h4 {
  margin: 0;
  color: #2c3e50;
  font-size: 16px;
}

.preview-summary {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.summary-item {
  padding: 16px;
  border-radius: 10px;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.summary-item.success {
  background: #f0f9eb;
  border: 1px solid #c2e7b0;
}

.summary-item.warning {
  background: #fdf6ec;
  border: 1px solid #f5dab1;
}

.summary-item.info {
  background: #ecf5ff;
  border: 1px solid #b3d8ff;
}

.summary-value {
  font-size: 24px;
  font-weight: 800;
}

.summary-item.success .summary-value { color: #67c23a; }
.summary-item.warning .summary-value { color: #e6a23c; }
.summary-item.info .summary-value { color: #409eff; }

.summary-label {
  font-size: 12px;
  color: #606266;
}

.preview-days {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;
}

.preview-day {
  padding: 12px 14px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e4e7ed;
}

.preview-day.can-create {
  background: #f7fbff;
  border-color: #b3d8ff;
}

.preview-day-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 6px;
  flex-wrap: wrap;
}

.preview-day-label {
  font-weight: 700;
  color: #2c3e50;
  font-size: 14px;
}

.preview-day-date {
  color: #606266;
  font-size: 13px;
}

.preview-day-badge {
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  margin-right: auto;
}

.badge-success {
  background: #f0f9eb;
  color: #67c23a;
}

.badge-warning {
  background: #fdf6ec;
  color: #e6a23c;
}

.preview-day-reason {
  font-size: 12px;
  color: #909399;
}

.empty-preview {
  text-align: center;
  padding: 20px;
  color: #e6a23c;
  background: #fdf6ec;
  border-radius: 8px;
  font-size: 14px;
}

/* ========================================== */
/* ریسپانسیو */
/* ========================================== */
@media (max-width: 768px) {
  .form-row {
    flex-direction: column;
  }

  .preview-summary {
    grid-template-columns: 1fr;
  }

  .summary-item {
    padding: 12px;
  }

  .summary-value {
    font-size: 20px;
  }
}
</style>